import React, { useMemo } from 'react'
import { Search } from 'lucide-react'
import ChipButton from '@/shared/components/ui/Button/ChipButton'
import { PrimaryButton } from '@/shared/components/ui/Button/PrimaryButton'
import SecondaryButton from '@/shared/components/ui/Button/SecondaryButton'
import { PART_LABELS, detectParts, removePart, type QueryCriteria } from '../queryParts'

const ChipButtonComponent = ChipButton as React.ComponentType<any>
const PrimaryButtonComponent = PrimaryButton as React.ComponentType<any>
const SecondaryButtonComponent = SecondaryButton as React.ComponentType<any>

interface NoResultsStateProps {
    /** The research ran to completion (vs. not run yet). */
    finished: boolean
    queryText: string
    criteria?: QueryCriteria | null
    onSearch?: (query: string) => void
    onRunQuery?: () => void
    onNewSearch?: () => void
}

/** Empty results: says whether research is pending or found nothing, and offers to loosen a filter. */
export default function NoResultsState({
    finished,
    queryText,
    criteria,
    onSearch,
    onRunQuery,
    onNewSearch,
}: NoResultsStateProps) {
    const loosening = useMemo(
        () => (finished ? detectParts(queryText, criteria) : []),
        [finished, queryText, criteria],
    )

    return (
        <div className='rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-12 flex flex-col items-center justify-center text-center shadow-xs'>
            <div className='w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400 mb-3'>
                <Search size={22} />
            </div>
            <h3 className='text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1'>
                {finished ? 'No companies matched all of these criteria' : 'No accounts matched this query yet'}
            </h3>
            <p className='text-xs text-gray-500 dark:text-gray-400 max-w-sm mb-4'>
                {finished
                    ? 'The research finished but found no company that fits every filter. Narrow filters like funding, company size or location are the usual cause.'
                    : 'AI will research public company information and keep the results here until you add an account.'}
            </p>

            {loosening.length > 0 && onSearch && (
                <div className='mb-4 flex max-w-xl flex-col items-center gap-2'>
                    <span className='text-xs font-medium text-gray-500 dark:text-gray-400'>Try searching again without:</span>
                    <div className='flex flex-wrap items-center justify-center gap-1.5'>
                        {loosening.map((part) => (
                            <ChipButtonComponent
                                key={`${part.start}-${part.kind}`}
                                tooltip={`Searches again without this ${PART_LABELS[part.kind].toLowerCase()}`}
                                onClick={() => {
                                    const loosened = removePart(queryText, part)
                                    if (loosened) onSearch(loosened)
                                }}
                            >
                                {part.text}
                            </ChipButtonComponent>
                        ))}
                    </div>
                </div>
            )}

            <div className='flex items-center gap-2'>
                {onRunQuery && (
                    <PrimaryButtonComponent startContent={false} onClick={onRunQuery}>
                        Run Research Now
                    </PrimaryButtonComponent>
                )}
                {onNewSearch && <SecondaryButtonComponent title='New Search' onClick={onNewSearch} />}
            </div>
        </div>
    )
}
